import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { UFOv2 } from '@atlassian/post-office-frontend-performance-tracking/v2';
import {
	MessageContextProvider,
	type MessageContextData,
} from '@atlassian/post-office-context/message-context';
import { RecommendationProvider } from '@atlassian/post-office-context/recommendation-context';
import type { MessageCreationType } from '@post-office/message-template-contracts/message-template';
import type { InAppNarrowExposureExperiment } from '@post-office/message-template-contracts/message-template-placement-config';
import type { RecommendationSession } from '@post-office/message-template-contracts/recommendations';
import {
	type FunctionComponent,
	type ReactNode,
	Suspense,
	type SuspenseProps,
	useMemo,
} from 'react';
import { ChoreographerWrapper } from './choreographer-wrapper';
import { MaybeChoreographerWrapper } from './maybe-choreographer-wrapper';
import { FeatureFlaggedUfoWrapper } from './ufo-wrapper';
import { MaybeTriggeredMessageValidator } from './maybe-triggered-message-validator';
import { MessageAnalyticsContextProvider } from '../message-analytics-context';
import { MaybeManualMessageViewedEvent } from '@post-office/placement-contexts/maybe-manual-message-viewed-event';
import { type MessageCategory } from '../types';
import type { WaitForTriggerFunction } from '../hooks/use-wait-for-trigger';
import { type JSX } from 'react/jsx-runtime';
import { useSubscriptionConfig } from '../create-placement-component/providers/stable-graphql-subscription';

type ComponentProps = {
	transactionAccountId?: string;
};

type ProviderProps = {
	messageTemplateId: string;
	messageInstanceId: string;
	messageCategory: MessageCategory;
	transactionAccountId?: string;
	recommendationSession?: RecommendationSession;
	product?: string;
	loadingFallback?: React.ReactNode;
	postOffice?: {
		analyticsDetails?: MessageContextData['analyticsDetails'];
	};
	triggerId?: string;
	messageCreationType?: MessageCreationType;
	messageSuccessComponent?: JSX.Element;
	componentProps: ComponentProps;
	isUFOEnabled: boolean;
	children?: ReactNode;
	SuspenseBoundary?: FunctionComponent<SuspenseProps>;
	waitForTrigger?: WaitForTriggerFunction;
	configSetId?: string;
	journeyId?: string;
	journeyTriggerId?: string;
	inAppNarrowExposureExperiment?: InAppNarrowExposureExperiment;
};

const DEFAULT_RECOMMENDATION_SESSION = {};

// https://switcheroo.atlassian.com/ui/gates/f85b4999-0bc2-4a97-85ef-821877ca73fc/key/post-office_enable_narrow-exposure-v2
const NARROW_EXPOSURE_V2_GATE = 'post-office_enable_narrow-exposure-v2';

export const MessageRendererProvider = ({
	messageInstanceId,
	messageTemplateId,
	messageCategory,
	recommendationSession,
	messageSuccessComponent,
	componentProps,
	loadingFallback,
	triggerId,
	messageCreationType,
	postOffice,
	isUFOEnabled,
	children,
	SuspenseBoundary = Suspense,
	waitForTrigger,
	configSetId,
	journeyId,
	journeyTriggerId,
	inAppNarrowExposureExperiment,
}: ProviderProps): JSX.Element => {
	const subscriptionConfig = useSubscriptionConfig();
	const subscriptionEnabled = subscriptionConfig.subscription?.enabled ?? false;

	/**
	 * Type guard: messageContextMemo must match MessageContextData exactly.
	 * If a field is missing or wrong type, the type annotation will catch it.
	 */
	const loadingComponent = useMemo(
		() =>
			isUFOEnabled ? (
				<UFOv2.UFOLoadHold name={UFOv2.HoldNames.MESSAGE}>
					{loadingFallback ?? null}
				</UFOv2.UFOLoadHold>
			) : (
				<>{loadingFallback ?? null}</>
			),
		[loadingFallback, isUFOEnabled],
	);

	const messageContextMemo = useMemo<MessageContextData>(
		() => ({
			messageInstanceId,
			messageTemplateId,
			triggerId,
			messageCreationType,
			transactionAccountId: componentProps.transactionAccountId,
			analyticsDetails: {
				...postOffice?.analyticsDetails,
				subscriptionEnabled,
			},
			journeyId,
			journeyTriggerId,
			configSetId,
		}),
		[
			messageInstanceId,
			messageTemplateId,
			triggerId,
			messageCreationType,
			componentProps.transactionAccountId,
			postOffice?.analyticsDetails,
			subscriptionEnabled,
			journeyId,
			journeyTriggerId,
			configSetId,
		],
	);

	const message = (
		<SuspenseBoundary fallback={loadingComponent}>
			<MaybeTriggeredMessageValidator waitForTrigger={waitForTrigger}>
				<MessageAnalyticsContextProvider>
					<MaybeManualMessageViewedEvent isManual={!!waitForTrigger}>
						{!waitForTrigger && messageSuccessComponent}
						{children}
					</MaybeManualMessageViewedEvent>
				</MessageAnalyticsContextProvider>
			</MaybeTriggeredMessageValidator>
		</SuspenseBoundary>
	);

	const withMessageContext = (content: ReactNode) => (
		<MessageContextProvider value={messageContextMemo}>
			<RecommendationProvider value={recommendationSession ?? DEFAULT_RECOMMENDATION_SESSION}>
				{content}
			</RecommendationProvider>
		</MessageContextProvider>
	);

	const isNarrowExposureV2Enabled = FeatureGates.checkGate(NARROW_EXPOSURE_V2_GATE);

	// MaybeChoreographerWrapper sits above the lazy template so it can claim its slot while it loads;
	// it no-ops for client-triggered messages, which choreograph via MaybeTriggeredMessageValidator.
	const withChoreographer = (content: ReactNode) => (
		<MaybeChoreographerWrapper
			ChoreographerWrapper={ChoreographerWrapper}
			enabled={!waitForTrigger}
			messageId={messageInstanceId}
			messageTemplateId={messageTemplateId}
			messageCategory={messageCategory}
			inAppNarrowExposureExperiment={
				isNarrowExposureV2Enabled ? inAppNarrowExposureExperiment : undefined
			}
		>
			<FeatureFlaggedUfoWrapper messageTemplateId={messageTemplateId} isEnabled={isUFOEnabled}>
				{content}
			</FeatureFlaggedUfoWrapper>
		</MaybeChoreographerWrapper>
	);

	// A narrow exposure shouldClaim needs message and recommendation context above the
	// choreographer to post a dropped event for this message.
	return isNarrowExposureV2Enabled
		? withMessageContext(withChoreographer(message))
		: withChoreographer(withMessageContext(message));
};
